<template lang="html">
  <div class="">
    <si-container :back="`/calculations/${calculatorId}`">
      <v-flex xs12>
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>Calculation: {{ calculationId }}</v-toolbar-title>
            <v-spacer />

            <v-tooltip
              v-for="(action, i) in actions.filter(a => a.primary)"
              :key="i"
              bottom
            >
              <template #activator="{ on }">
                <v-btn 
                  text
                  icon 
                  :color="action.color" 
                  :disabled="action.disabled" 
                  :loading="action.loading" 
                  v-on="on"
                  @click="action.callback"
                >
                  <v-icon>{{ action.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ action.text }}</span>
            </v-tooltip>

            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>more_vert</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item 
                  v-for="(action, i) in actions.filter(a => !a.primary)"
                  :key="i"
                  @click="action.callback"
                >
                  <v-list-item-title>{{ action.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <SiTestCaseBuilder
              v-model="testCaseDialog"
              :calculator-id="calculatorId"
              :test-case="testCaseData"
              show-confirm
            />
          </v-toolbar>
          <v-divider />
          <!-- <v-card-title primary-title>
          Calculation ID: {{calculationId}}
          <v-spacer></v-spacer>
        </v-card-title> -->
          <v-container
            fluid
            pa-4
          >
            <v-layout
              v-if="!_loader"
              wrap
            >
              <v-flex xs12>
                <div class="caption grey--text">
                  Status
                </div>
                <v-layout
                  align-center
                  ma-0
                >
                  <v-icon
                    left
                    :color="calculation._calculated ? 'success' : 'error'"
                  >
                    {{ calculation._calculated ? 'check_circle' : 'error' }}
                  </v-icon>{{ calculation._calculated ? 'SUCCESS': 'ERROR' }}
                </v-layout>
              </v-flex>
              <v-flex
                v-if="calculation._assisted || calculation._assigned"
                xs12
              >
                <div class="caption grey--text">
                  Attributes
                </div>
                <v-layout
                  v-if="calculation._assisted"
                  align-center
                  ma-0
                >
                  <v-icon left>
                    support_agent
                  </v-icon>
                  Assisted
                </v-layout>
                <v-layout
                  v-if="calculation._assigned"
                  align-center
                  ma-0
                >
                  <v-icon left>
                    forward_to_inbox
                  </v-icon>
                  Assigned
                </v-layout>
              </v-flex>
              <v-flex xs12>
                <div class="caption grey--text">
                  Date
                </div>
                {{ _formatDateTime(calculation.timestamp) }}
              </v-flex>
              <v-flex xs12>
                <div class="caption grey--text">
                  Client Email
                </div>
                <v-layout
                  v-if="client.email"
                  ma-0
                  align-center
                >
                  {{ client.email }}
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn
                        text
                        icon
                        small
                        class="ml-2"
                        color="primary"
                        :to="clientPath(calculation.client.email)"
                        :disabled="!calculation.client"
                        v-on="on"
                      >
                        <v-icon>track_changes</v-icon>
                      </v-btn>
                    </template>
                    <span>View client path</span>
                  </v-tooltip>
                </v-layout>
                <v-layout
                  v-else
                  ma-0
                  align-center
                >
                  <div class="grey--text">
                    NOT PURCHASED
                  </div>
                </v-layout>
              </v-flex>
              <v-flex
                v-if="client.reference"
                xs12
              >
                <div class="caption grey--text">
                  Client Reference
                </div>
                <div>{{ client.reference }}</div>
              </v-flex>
              <v-flex xs12>
                <div class="caption grey--text">
                  Report access
                </div>
                <v-layout
                  ma-0
                  align-center
                >
                  <v-icon>{{ calculation._unrestricted ? 'lock_open' : 'lock' }}</v-icon>
                  {{ calculation._unrestricted ? 'NOT RESTRICTED' : 'RESTRICTED' }}
                </v-layout>
              </v-flex>
              <v-flex
                v-if="calculation._migrated"
                xs12
              >
                <div class="caption grey--text">
                  Migrated
                </div>
                <div>
                  <v-icon :color="(calculation.result || {}).value === (calculation.result_legacy || {}).value ? 'success' : 'error'">
                    {{ (calculation.result || {}).value === (calculation.result_legacy || {}).value ? 'sync' : 'sync_problem' }}
                  </v-icon>
                  {{ (calculation.result || {}).value === (calculation.result_legacy || {}).value ? 'Migration successful - results accurate' : 'Migration error - results inaccurate' }}
                </div>
              </v-flex>
              <v-flex
                v-if="!calculation._unrestricted"
                xs12
              >
                <div class="caption grey--text">
                  View Links
                </div>
                <div
                  v-for="token in _sortObject(calculation.view, 'created')"
                  :key="token.created"
                  :class="{'success--text': !tokenExpired(token), 'grey--text ': tokenExpired(token)}"
                >
                  {{ _formatDateTime(token.created) }} - Used: {{ token.used ? token.used : token.active === false ? 1 : 0 }}
                </div>
              </v-flex>
              <v-flex
                v-if="(calculation.feedback || {}).rating"
                xs12
              >
                <div class="caption grey--text">
                  Feedback
                </div>
                <v-layout align-center>
                  <v-flex
                    shirnk
                    pa-0
                  >
                    <v-rating
                      :value="(calculation.feedback.ratings || {}).overall"
                      :length="5"
                      half-increments
                      readonly
                      color="yellow darken-2"
                      background-color="grey darken-"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    class="body-2"
                  >
                    Overall
                  </v-flex>
                </v-layout>
                <v-layout align-center>
                  <v-flex
                    shirnk
                    pa-0
                  >
                    <v-rating
                      :value="(calculation.feedback.ratings || {}).ease"
                      :length="5"
                      half-increments
                      readonly
                      color="yellow darken-2"
                      background-color="grey darken-"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    class="body-2"
                  >
                    Ease of use
                  </v-flex>
                </v-layout>
                <v-layout align-center>
                  <v-flex
                    shirnk
                    pa-0
                  >
                    <v-rating
                      :value="(calculation.feedback.ratings || {}).report"
                      :length="5"
                      half-increments
                      readonly
                      color="yellow darken-2"
                      background-color="grey darken-"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    class="body-2"
                  >
                    Quality of report
                  </v-flex>
                </v-layout>
                <v-layout v-if="calculation.feedback.text">
                  <v-flex shrink>
                    <v-icon color="grey">
                      feedback
                    </v-icon>
                  </v-flex>
                  <v-flex x1s2>
                    {{ calculation.feedback.text }}
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex xs12>
                <v-divider />
              </v-flex>
              <v-flex xs12>
                <v-btn
                  text
                  color="primary"
                  @click="viewReport = !viewReport"
                >
                  View Report
                  <v-icon right>
                    pageview
                  </v-icon>
                </v-btn>
              
                <v-btn
                  text
                  color="primary"
                  @click.stop="_viewReportWindow(calculatorId, calculationId)"
                >
                  Open Report
                  <v-icon right>
                    launch
                  </v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12>
                <v-divider />
              </v-flex>
              <v-flex xs12>
                <v-treeview
                  :items="_treeViewObject(calculation)"
                  open-on-click
                  dense
                >
                  <template #label="{ item }">
                    <div v-html="item.html" />
                  </template>
                </v-treeview>
              </v-flex>

              <v-flex
                v-if="calculation.error"
                xs12
              >
                <v-alert
                  prominent
                  type="error"
                >
                  <pre>{{ calculation.error.stack }}</pre>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-flex>
      <SiReportDialog
        v-model="viewReport"
        :calculation-id="calculationId"
        :calculator-id="calculatorId"
      />

      <v-dialog
        v-model="assign"
        persistent
        max-width="600px"
      >
        <v-card>
          <v-card-title>
            <span class="headline">Assign calculation to client</span>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md>
              <v-form
                ref="form"
                v-model="assignClientValid"
              >
                <v-layout wrap>
                  <v-flex
                    xs6
                    pb-0
                  >
                    <SiInputText
                      v-model="assignClient.firstName"
                      outlined
                      label="First name"
                      required
                      :rules="[_ruleRequired('First name is required')]"
                    />
                  </v-flex>
                  <v-flex
                    xs6
                    pb-0
                  >
                    <SiInputText
                      v-model="assignClient.lastName"
                      outlined
                      label="Last name"
                      required
                      :rules="[_ruleRequired('Last name is required')]"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    py-0
                  >
                    <SiInputText
                      v-model="assignClient.email"
                      outlined
                      label="Email"
                      required
                      :rules="[_ruleRequired('A valid email is required'), _ruleEmail()]"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    py-0
                  >
                    <SiInputText
                      v-model="assignClient.reference"
                      outlined
                      label="client reference"
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    py-0
                  >
                    <SiInputSelect
                      v-model="assignClient.type"
                      outlined
                      label="Who did the client complete this calculation for?"
                      :items="[{text: 'For themselves (personal)', value: 'personal'},{text: 'For a client (agent)', value: 'agent'}]"
                      required
                      :rules="[_ruleRequired('You must nominate who you are completing this calculation for')]"
                    />
                  </v-flex>
                </v-layout>
              </v-form>
              <div> Client will notified immediately and given full access to this calculation.</div>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="grey"
              text
              @click="assign = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              text
              :loading="assignLoading"
              @click="assignToClient"
            >
              Assign to client
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-snackbar
        v-model="resent"
        :timeout="3000"
        color="success"
        top
        right
      >
        <v-icon left>
          email
        </v-icon> Report has been resent
      </v-snackbar>
    </si-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { rtdb, auth } from '@/plugins/firebase'
import helpers from '@/mixins/helpers'
import validation from '@/mixins/validations'
import links from '@/mixins/links'

export default {
  mixins: [ helpers, links, validation ],
  data () {
    return {
      assign: false,
      assignClient: {},
      assignClientValid: false,
      assignLoading: false,
      calculation: {},
      viewReport: false,
      testCaseDialog: false,
      testCaseData: {},
      resent: false,
      resendDisable: false,
    }
  },
  computed: {
    ...mapGetters(['_calculators', '_loader', '_userToken']),
    actions () {
      return [
        ...( !this.client.email 
        ? [{text: 'Assign calculation to client', icon: 'forward_to_inbox', callback: () => this.assign = true, primary: true, color: 'primary'}] 
        : [{text: 'Resend report email', icon: 'email', callback: this.resendReportEmail, primary: true, loading: this.resendDisable}]
        ),
        {text: 'Load data into new calculation', icon: 'open_in_browser', callback: this.loadCalculation, primary: true},
        {text: 'Create as test', icon: 'open_in_browser', callback: this.createTestCase, primary: false}, 
      ]
    },
    calculationId () {
      return this.$route.params.id
    },
    calculatorId () {
      return this.$route.params.sub
    },
    calculationPath () {
      return `calculations/${this.calculatorId}/${this.calculationId}/`
    },
    client () {
      return this.calculation.client || {}
    }
  },
  watch: {
    '$route.params.id' () {
      this.getCalculation()
    }
  },
  beforeMount () {
    this.getCalculation()
  },
  methods: {
    clientPath (email) {
      return `/clientpath/identity/${btoa(email)}`
    },
    getCalculation () {
      this.calculation = {}
      this.$store.commit('set_loader', true)
      rtdb.ref(this.calculationPath).once('value', s => {
        this.calculation = s.val() || {}
        this.$store.commit('set_loader', false)
      })
    },
    assignToClient () {
      if (this.$refs.form.validate() && !this.client.email) {
        this.assignLoading = true
        let updates = {}

        updates[`${this.calculationPath}/_paid`] = true
        updates[`${this.calculationPath}/_assigned`] = true
        updates[`${this.calculationPath}/client`] = this.assignClient

        rtdb.ref().update(updates).then(() => {
          this.getCalculation()
          this.assign = false
          this.assignLoading = false
          this.assignClient = {}
        })
      }
    },
    createTestCase () {
      let { calculationData:data, audit, report, result } = this.calculation
      let id = this.calculationId
      this.testCaseData = { id, data, target_calculation: { audit, report, result }, tests: {} }
      this.testCaseDialog = true
    },
    async loadCalculation () {
      const token = await auth.currentUser.getIdToken()
      let variant = this.calculation._variant
      
      window.open(`${process.env.VUE_APP_BASE_URL}/calculator/${this.calculatorId}/${variant}?calculationId=${this.calculationId}&token=${token}`)
    },
    tokenExpired ({expiry}) {
      return Number(new Date()) > Number(new Date(expiry))
    },
    resendReportEmail () {
      rtdb.ref(`${this.calculationPath}/resend`).push({ timestamp: new Date().toISOString() })
      this.resendDisable = true
      setTimeout(() => {
        this.resent = true
        this.resendDisable = false
      }, 1500);
    }
  }
}
</script>

<style lang="css" scoped>

</style>
